import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from "@brg/ui";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { BaseSyntheticEvent, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectEditPageGridRow } from "shared/pages/projectEdit/styles";
import CheckLongreadRadioButton from "./CheckLongreadRadioButton";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { API, Designer, DesignerAPI, LangObject } from "@brg/api";
import { convertDesignerFromGlobalToLocal } from "shared/pages/projectEdit/utils";
import { ChannelDescription, LangBlockWrapper, TeamEditLabel } from "../styles";
import { AuthorT } from "shared/pages/projectEdit/types";
import { InputType, LangTitleBlock } from "shared/components/LangTitleBox/LangTitleBox";
import { LayoutConst } from "core";
import styled from "styled-components";
import { ModerationType } from "../const/ModerationTypes";
import { MagicBoxRow } from "shared/pages/project/components/toolsStyles";
import setTextByLang from "shared/helpers/setTextByLang";
import { limitTextWithErrorToast } from "../helpers/limitTextWithErrorToast";
import { textAllowed } from "../helpers/textAllowed";

export const Wrapper = styled(FlexBox)`
    @media(max-width: ${LayoutConst.screen.m}px) {
        .lang-row-container {
            flex-flow: column;

            .lang-label-block {
                margin-bottom: 8px;
            }
        }
    }
`;

export enum CHAT_CLOSED_STATUS {
    open = 0,
    pause = 1,
    close = 2
}

export const ChatPermissionEdit: FC<{ 
    channelType: ModerationType,
    whiteListIds: string[], 
    blackListIds: string[],
    channelCommentsMsg: LangObject[], 
    status: CHAT_CLOSED_STATUS,
    onChange: (data: any, fieldName?: string) => void 
}> = ({
    channelType,
    whiteListIds,
    blackListIds,
    channelCommentsMsg,
    status,
    onChange
}) => {
    const { t, i18n: {language} } = useTranslation();
    const toasts = useToasts();
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const isS = useBreakpoint(LayoutConst.screen.s - 1);
    const [whiteListUsers, setWhiteList] = useState<AuthorT[]>([]);
    const [blackListUsers, setBlackList] = useState<AuthorT[]>([]);
    const [chatStatus, setChatStatus] = useState<CHAT_CLOSED_STATUS>(CHAT_CLOSED_STATUS.open);
    const [chatPermission, setChatPermisson] = useState(false);
    const [closedMsg, setClosedMsg] = useState<LangObject[]>([]);

    const isAutoChannel = useMemo(() => {
        return channelType === ModerationType.ChannelGroupOrCourse
    } , [channelType])

    console.log('tst auto', isAutoChannel)

    const onClosedMsgChange = (ev: BaseSyntheticEvent, lang?: string) => {
        textAllowed(ev, (val) => onChange(
            setTextByLang(
                closedMsg, 
                lang || 'ru', 
                limitTextWithErrorToast(val, 100, toasts, t)
            ),
            'channelCommentMsg'
        ))
    }

    const onChatStatusChange = (flag: any) => {
        onChange(flag, 'status')
    }

    const onChatPermissionChange = (flag: boolean) => {
        if (!flag) {
            onChange([], 'whiteListIds')
            onChange([], 'blackListIds')
        }
        setChatPermisson(flag);
    }

    const onWhiteUsersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        onChange(authors?.map((user) => user?.accountId), 'whiteListIds')
    }

    const onBlackUsersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        onChange(authors?.map((user) => user?.accountId), 'blackListIds')
    }

    const fetchUsers = async (list: string[], callback: (value: any) => void) => {
        if (!list?.length) {
            callback([]);
            return;
        }

        await DesignerAPI.GetList({
            designerIds: list
        })
        .then((r) => r.json())
        .then((r) => {
            //@ts-ignore
            const converted = r?.data?.map((user: Designer<'Global'>) => convertDesignerFromGlobalToLocal(user, language))
            callback(converted)
        })
    }

    useEffect(() => {
        fetchUsers(whiteListIds, (value: any) => setWhiteList(value))
        fetchUsers(blackListIds, (value: any) => setBlackList(value))
        setClosedMsg(channelCommentsMsg)
        setChatStatus(status)
    } , [whiteListIds, blackListIds, channelCommentsMsg, status])

    return (
        <Wrapper offsetTop={32} offsetTopMobile={16} direction="column" gy={24} gyMobile={16}>
            <FlexBox  directionMobile="column">
                <TeamEditLabel isTeamEdit style={{ marginBottom: 8}}>
                    {t('chat.adminPage.statusText.label')}
                </TeamEditLabel>
                <ProjectEditPageGridInput direction="column" directionMobile="column" isTeamEdit isColumns>
                    <CheckLongreadRadioButton
                        onChange={() => onChatPermissionChange(false)}
                        name=""
                        title={t('chat.adminPage.statusText.radio_1')}
                        checked={!chatPermission && !whiteListUsers?.length}
                    />
                    <CheckLongreadRadioButton
                        onChange={() => onChatPermissionChange(true)}
                        name=""
                        title={t('chat.adminPage.statusText.radio_2')}
                        checked={!!chatPermission || !!whiteListUsers?.length }
                    />

                </ProjectEditPageGridInput>
            </FlexBox>
            {(chatPermission || !!whiteListUsers?.length )&& <FlexBox>
                <ProjectAuthorsEdit
                    label={t('chat.adminPage.whiteList.label')}
                    value={whiteListUsers}
                    onChange={onWhiteUsersChange}
                    placeholder={t('search_by_name')}
                    orientation="wide"
                    isTeamEdit
                    isAdminPage
                    errorMsg={t('teams.adminPage.errors.equal_owners')}
                    // teamModerationType={teamModerationType.value}
                />
            </FlexBox>}
            <FlexBox>
                <ChannelDescription>
                    {t(`teams.adminPage.additionalFields.${isAutoChannel ? 'channel_block' : 'channel_block_feed'}`)}
                </ChannelDescription>
            </FlexBox>
            <FlexBox>
                <ProjectAuthorsEdit
                        label={t('chat.adminPage.blackList.label')}
                        value={blackListUsers} 
                        onChange={onBlackUsersChange}
                        placeholder={t('search_by_name')}
                        orientation="wide"
                        isTeamEdit
                        isAdminPage
                        errorMsg={t('teams.adminPage.errors.equal_owners')}
                        // teamModerationType={teamModerationType.value}
                    />
            </FlexBox>

            <FlexBox>
                <ChannelDescription>
                    {t(`teams.adminPage.additionalFields.${isAutoChannel ? 'channel_closed' : 'feed_closed'}`)}
                </ChannelDescription>
            </FlexBox>
            <FlexBox  directionMobile="column">
                <TeamEditLabel isTeamEdit style={{ marginBottom: 8}}>
                    {t(`chat.adminPage.permission.${isAutoChannel ? 'label' : 'comments_label'}`)}
                </TeamEditLabel>
                {isAutoChannel ? (
                    <ProjectEditPageGridInput  direction="column" directionMobile="column" isTeamEdit isColumns>
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.open)}
                            name=""
                            title={t('chat.adminPage.permission.radio_1')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.open}
                        />
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.pause)}
                            name=""
                            title={t('chat.adminPage.permission.radio_2')}
                            hintText={t('chat.adminPage.permission.radio_2_info')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.pause}
                        />
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.close)}
                            name=""
                            title={t('chat.adminPage.permission.radio_3')}
                            hintText={t('chat.adminPage.permission.radio_3_info')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.close}
                        />
                    </ProjectEditPageGridInput>
                ) : (
                    <FlexBox direction="row" directionMobile="row" justify="flex-start" align="center" offsetLeftMobile={0}>
                        <MagicBoxRow
                            className={'checkbox'}
                            checked={chatStatus === CHAT_CLOSED_STATUS.close}
                            label={''}
                            onClick={() => {
                                onChatStatusChange(
                                    chatStatus === CHAT_CLOSED_STATUS.open ? 
                                    CHAT_CLOSED_STATUS.close : CHAT_CLOSED_STATUS.open )
                            }}
                            isTeamEdit
                        />
                        
                    </FlexBox>
                )}

            </FlexBox>
            {isAutoChannel && (
                <>
                    {chatStatus === CHAT_CLOSED_STATUS.pause && (
                        <LangBlockWrapper direction={'row'} directionMobile={isS ? 'column' : 'row'} maxSize={904}>
                            <ProjectEditPageGridRow style={{ width: '100%' }} isTeamEdit>
                                <CommonInput 
                                    type={INPUT_TYPES.textarea}
                                    title={t('chat.adminPage.permission.comments')}
                                    orientation="wide"
                                    maxLength={800}
                                    minHeight={265}
                                    onChange={(ev: any, lang?: string) => onClosedMsgChange(ev, lang)}
                                    useLangSwitcher
                                    value={closedMsg}
                                    placeholder={''}
                                />
                            </ProjectEditPageGridRow>
                        </LangBlockWrapper>
                    )}
                </>
            )}
        </Wrapper>
    )
}import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from "@brg/ui";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { BaseSyntheticEvent, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectEditPageGridRow } from "shared/pages/projectEdit/styles";
import CheckLongreadRadioButton from "./CheckLongreadRadioButton";
import ProjectAuthorsEdit, { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { API, Designer, DesignerAPI, LangObject } from "@brg/api";
import { convertDesignerFromGlobalToLocal } from "shared/pages/projectEdit/utils";
import { ChannelDescription, LangBlockWrapper, TeamEditLabel } from "../styles";
import { AuthorT } from "shared/pages/projectEdit/types";
import { InputType, LangTitleBlock } from "shared/components/LangTitleBox/LangTitleBox";
import { LayoutConst } from "core";
import styled from "styled-components";
import { ModerationType } from "../const/ModerationTypes";
import { MagicBoxRow } from "shared/pages/project/components/toolsStyles";
import setTextByLang from "shared/helpers/setTextByLang";
import { limitTextWithErrorToast } from "../helpers/limitTextWithErrorToast";
import { textAllowed } from "../helpers/textAllowed";

export const Wrapper = styled(FlexBox)`
    @media(max-width: ${LayoutConst.screen.m}px) {
        .lang-row-container {
            flex-flow: column;

            .lang-label-block {
                margin-bottom: 8px;
            }
        }
    }
`;

export enum CHAT_CLOSED_STATUS {
    open = 0,
    pause = 1,
    close = 2
}

export const ChatPermissionEdit: FC<{ 
    channelType: ModerationType,
    whiteListIds: string[], 
    blackListIds: string[],
    channelCommentsMsg: LangObject[], 
    status: CHAT_CLOSED_STATUS,
    onChange: (data: any, fieldName?: string) => void 
}> = ({
    channelType,
    whiteListIds,
    blackListIds,
    channelCommentsMsg,
    status,
    onChange
}) => {
    const { t, i18n: {language} } = useTranslation();
    const toasts = useToasts();
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const isS = useBreakpoint(LayoutConst.screen.s - 1);
    const [whiteListUsers, setWhiteList] = useState<AuthorT[]>([]);
    const [blackListUsers, setBlackList] = useState<AuthorT[]>([]);
    const [chatStatus, setChatStatus] = useState<CHAT_CLOSED_STATUS>(CHAT_CLOSED_STATUS.open);
    const [chatPermission, setChatPermisson] = useState(false);
    const [closedMsg, setClosedMsg] = useState<LangObject[]>([]);

    const isAutoChannel = useMemo(() => {
        return channelType === ModerationType.ChannelGroupOrCourse
    } , [channelType])

    console.log('tst auto', isAutoChannel)

    const onClosedMsgChange = (ev: BaseSyntheticEvent, lang?: string) => {
        textAllowed(ev, (val) => onChange(
            setTextByLang(
                closedMsg, 
                lang || 'ru', 
                limitTextWithErrorToast(val, 100, toasts, t)
            ),
            'channelCommentMsg'
        ))
    }

    const onChatStatusChange = (flag: any) => {
        onChange(flag, 'status')
    }

    const onChatPermissionChange = (flag: boolean) => {
        if (!flag) {
            onChange([], 'whiteListIds')
            onChange([], 'blackListIds')
        }
        setChatPermisson(flag);
    }

    const onWhiteUsersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        onChange(authors?.map((user) => user?.accountId), 'whiteListIds')
    }

    const onBlackUsersChange = ({ authors }: ProjectAuthorsChangeCbT) => {
        onChange(authors?.map((user) => user?.accountId), 'blackListIds')
    }

    const fetchUsers = async (list: string[], callback: (value: any) => void) => {
        if (!list?.length) {
            callback([]);
            return;
        }

        await DesignerAPI.GetList({
            designerIds: list
        })
        .then((r) => r.json())
        .then((r) => {
            //@ts-ignore
            const converted = r?.data?.map((user: Designer<'Global'>) => convertDesignerFromGlobalToLocal(user, language))
            callback(converted)
        })
    }

    useEffect(() => {
        fetchUsers(whiteListIds, (value: any) => setWhiteList(value))
        fetchUsers(blackListIds, (value: any) => setBlackList(value))
        setClosedMsg(channelCommentsMsg)
        setChatStatus(status)
    } , [whiteListIds, blackListIds, channelCommentsMsg, status])

    return (
        <Wrapper offsetTop={32} offsetTopMobile={16} direction="column" gy={24} gyMobile={16}>
            <FlexBox  directionMobile="column">
                <TeamEditLabel isTeamEdit style={{ marginBottom: 8}}>
                    {t('chat.adminPage.statusText.label')}
                </TeamEditLabel>
                <ProjectEditPageGridInput direction="column" directionMobile="column" isTeamEdit isColumns>
                    <CheckLongreadRadioButton
                        onChange={() => onChatPermissionChange(false)}
                        name=""
                        title={t('chat.adminPage.statusText.radio_1')}
                        checked={!chatPermission && !whiteListUsers?.length}
                    />
                    <CheckLongreadRadioButton
                        onChange={() => onChatPermissionChange(true)}
                        name=""
                        title={t('chat.adminPage.statusText.radio_2')}
                        checked={!!chatPermission || !!whiteListUsers?.length }
                    />

                </ProjectEditPageGridInput>
            </FlexBox>
            {(chatPermission || !!whiteListUsers?.length )&& <FlexBox>
                <ProjectAuthorsEdit
                    label={t('chat.adminPage.whiteList.label')}
                    value={whiteListUsers}
                    onChange={onWhiteUsersChange}
                    placeholder={t('search_by_name')}
                    orientation="wide"
                    isTeamEdit
                    isAdminPage
                    errorMsg={t('teams.adminPage.errors.equal_owners')}
                    // teamModerationType={teamModerationType.value}
                />
            </FlexBox>}
            <FlexBox>
                <ChannelDescription>
                    {t(`teams.adminPage.additionalFields.${isAutoChannel ? 'channel_block' : 'channel_block_feed'}`)}
                </ChannelDescription>
            </FlexBox>
            <FlexBox>
                <ProjectAuthorsEdit
                        label={t('chat.adminPage.blackList.label')}
                        value={blackListUsers} 
                        onChange={onBlackUsersChange}
                        placeholder={t('search_by_name')}
                        orientation="wide"
                        isTeamEdit
                        isAdminPage
                        errorMsg={t('teams.adminPage.errors.equal_owners')}
                        // teamModerationType={teamModerationType.value}
                    />
            </FlexBox>

            <FlexBox>
                <ChannelDescription>
                    {t(`teams.adminPage.additionalFields.${isAutoChannel ? 'channel_closed' : 'feed_closed'}`)}
                </ChannelDescription>
            </FlexBox>
            <FlexBox  directionMobile="column">
                <TeamEditLabel isTeamEdit style={{ marginBottom: 8}}>
                    {t(`chat.adminPage.permission.${isAutoChannel ? 'label' : 'comments_label'}`)}
                </TeamEditLabel>
                {isAutoChannel ? (
                    <ProjectEditPageGridInput  direction="column" directionMobile="column" isTeamEdit isColumns>
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.open)}
                            name=""
                            title={t('chat.adminPage.permission.radio_1')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.open}
                        />
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.pause)}
                            name=""
                            title={t('chat.adminPage.permission.radio_2')}
                            hintText={t('chat.adminPage.permission.radio_2_info')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.pause}
                        />
                        <CheckLongreadRadioButton
                            onChange={() => onChatStatusChange(CHAT_CLOSED_STATUS.close)}
                            name=""
                            title={t('chat.adminPage.permission.radio_3')}
                            hintText={t('chat.adminPage.permission.radio_3_info')}
                            checked={chatStatus === CHAT_CLOSED_STATUS.close}
                        />
                    </ProjectEditPageGridInput>
                ) : (
                    <FlexBox direction="row" directionMobile="row" justify="flex-start" align="center" offsetLeftMobile={0}>
                        <MagicBoxRow
                            className={'checkbox'}
                            checked={chatStatus === CHAT_CLOSED_STATUS.close}
                            label={''}
                            onClick={() => {
                                onChatStatusChange(
                                    chatStatus === CHAT_CLOSED_STATUS.open ? 
                                    CHAT_CLOSED_STATUS.close : CHAT_CLOSED_STATUS.open )
                            }}
                            isTeamEdit
                        />
                        
                    </FlexBox>
                )}

            </FlexBox>
            {isAutoChannel && (
                <>
                    {chatStatus === CHAT_CLOSED_STATUS.pause && (
                        <LangBlockWrapper direction={'row'} directionMobile={isS ? 'column' : 'row'} maxSize={904}>
                            <ProjectEditPageGridRow style={{ width: '100%' }} isTeamEdit>
                                <CommonInput 
                                    type={INPUT_TYPES.textarea}
                                    title={t('chat.adminPage.permission.comments')}
                                    orientation="wide"
                                    maxLength={800}
                                    minHeight={265}
                                    onChange={(ev: any, lang?: string) => onClosedMsgChange(ev, lang)}
                                    useLangSwitcher
                                    value={closedMsg}
                                    placeholder={''}
                                />
                            </ProjectEditPageGridRow>
                        </LangBlockWrapper>
                    )}
                </>
            )}
        </Wrapper>
    )
}